import React, { useState } from 'react';
import CurrenciesPage from './pages/Currencies.tsx';
import BarcodeType1Page from './pages/BarcodeType1Page.tsx';
import BarcodeType2Page from './pages/BarcodeType2Page.tsx';
import VolumeDiscountPage from './pages/VolumeDiscountPage.tsx';
import ProductsPage from './pages/ProductsPage.tsx';
import FixedValuePage from './pages/FixedValuePage.tsx';
import ProcessesPage from './pages/ProcessesPage.tsx';
import CompositesPage from './pages/CompositesPage.tsx';
import CompositionEditPage from './pages/CompositionEditPage.tsx';
import SurchargesPage from './pages/SurchargesPage.tsx';
import CostsPage from './pages/CostsPage.tsx';
import PricesPage from './pages/PricesPage.tsx';
import FamiliesPage from './pages/FamiliesPage.tsx';
import SubFamiliesPage from './pages/SubFamiliesPage.tsx';
import UnitsOfMeasurePage from './pages/UnitsOfMeasurePage.tsx';
import VendorsPage from './pages/VendorsPage.tsx';
import SystemLogPage from './pages/SystemLogPage.tsx';
import AppHomePage from './pages/AppHomePage.tsx'; // UPDATED: Import the new AppHomePage
import { Button } from './components/ui/button.tsx';
import { BrowserRouter as Router, Routes, Route, Link, useLocation } from 'react-router-dom';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './components/ui/dropdown-menu.tsx';
import { cn } from './lib/utils.ts';
import { ErrorProvider } from './context/ErrorContext.tsx';
import ErrorModal from './components/ErrorModal.tsx';

interface PageEntry {
  component: React.FC;
  displayName: string;
  path: string;
}

type ActivePage = 'home' | 'currencies' | 'fixedValues' | 'BarcodeType1' | 'BarcodeType2' | 'VolumeDiscount' | 'Products' | 'processes' | 'composites' | 'surcharges' | 'costs' | 'prices' | 'families' | 'subfamilies' | 'unitsOfMeasure' | 'vendors' | 'systemLog';

const pageComponents: Record<ActivePage, PageEntry> = {
  home: {
    component: AppHomePage, // UPDATED: Use the new AppHomePage
    displayName: 'Home',
    path: '/app',
  },
  currencies: {
    component: CurrenciesPage,
    displayName: 'Currencies',
    path: '/currencies',
  },
  surcharges: {
    component: SurchargesPage,
    displayName: 'Surcharges',
    path: '/surcharges',
  },
  families: {
    component: FamiliesPage,
    displayName: 'Families',
    path: '/families',
  },
  subfamilies: {
    component: SubFamiliesPage,
    displayName: 'Subfamilies',
    path: '/subfamilies',
  },
  unitsOfMeasure: {
    component: UnitsOfMeasurePage,
    displayName: 'Units of Measure',
    path: '/units_of_measure',
  },
  vendors: {
    component: VendorsPage,
    displayName: 'Vendors',
    path: '/vendors',
  },
  fixedValues: {
    component: FixedValuePage,
    displayName: 'Fixed Values',
    path: '/fixed-values',
  },
  processes: {
    component: ProcessesPage,
    displayName: 'Processes',
    path: '/processes',
  },
  BarcodeType1: {
    component: BarcodeType1Page,
    displayName: 'Barcode Type 1s',
    path: '/barcode-type-1s',
  },
  BarcodeType2: {
    component: BarcodeType2Page,
    displayName: 'Barcode Type 2s',
    path: '/barcode-type-2s',
  },
  systemLog: {
    component: SystemLogPage,
    displayName: 'System Log',
    path: '/system_log',
  },
  Products: {
    component: ProductsPage,
    displayName: 'Products',
    path: '/products',
  },
  composites: {
    component: CompositesPage,
    displayName: 'Composite Products',
    path: '/composites',
  },
  VolumeDiscount: {
    component: VolumeDiscountPage,
    displayName: 'Volume Discounts',
    path: '/volume-discounts',
  },
  costs: {
    component: CostsPage,
    displayName: 'Costs',
    path: '/costs',
  },
  prices: {
    component: PricesPage,
    displayName: 'Prices',
    path: '/prices',
  },
};

const AppContent: React.FC = () => {
  const location = useLocation();
  // Determine active page based on current path
  const activePageId = (Object.keys(pageComponents) as ActivePage[]).find(
    (key) => location.pathname.startsWith(pageComponents[key].path)
  );

  const allMenuItems = Object.entries(pageComponents);

  // The first item is 'home'. The next 11 items will go into the "Settings" dropdown.
  const settingsMenuItems = allMenuItems.slice(1, 12);
  // The remaining items will be displayed directly in the main menu
  const mainMenuItems = allMenuItems.slice(12);

  return (
    <div className="flex flex-col min-h-screen">
      <div className="container mx-auto p-4 sm:p-6 lg:p-8 flex-grow">
        <nav className="mb-6 flex justify-center space-x-4 flex-wrap gap-2">
          <Button
            variant={activePageId === 'home' ? 'default' : 'outline'}
            asChild
          >
            <Link to="/app">Home</Link>
          </Button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline">Settings</Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-56">
              {settingsMenuItems.map(([pageId, pageInfo]) => (
                <DropdownMenuItem
                  key={pageId}
                  asChild
                  className={cn(
                    activePageId === pageId ? 'bg-accent text-accent-foreground' : '',
                    'cursor-pointer'
                  )}
                >
                  <Link to={pageInfo.path}>
                    {pageInfo.displayName}
                  </Link>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {mainMenuItems.map(([pageId, pageInfo]) => (
            <Button
              key={pageId}
              variant={activePageId === pageId ? 'default' : 'secondary'}
              asChild
            >
              <Link to={pageInfo.path}>
                {pageInfo.displayName}
              </Link>
            </Button>
          ))}
        </nav>

        <Routes>
          {Object.entries(pageComponents).map(([pageId, pageInfo]) => (
            <Route key={pageId} path={pageInfo.path} element={<pageInfo.component />} />
          ))}
          <Route path="/composition-edit/:id" element={<CompositionEditPage />} />
          <Route path="/" element={<AppHomePage />} /> {/* UPDATED: Point root to new AppHomePage */}
        </Routes>
      </div>

      <footer className="mt-12 py-8 bg-gray-100 text-center text-gray-700 border-t border-gray-200">
        <div className="container mx-auto px-4">
          <p className="mb-4">
            Have questions or want to learn more?
          </p>
          <p className="text-md mb-2">
            Contact us at <a href="mailto:hello@oskite.com" className="text-blue-600 hover:underline font-medium">costwand.app@gmail.com</a>
          </p>
        </div>
      </footer>
    </div>
  );
};

const App: React.FC = () => {
  return (
    <Router>
      <ErrorProvider>
        <AppContent />
        <ErrorModal />
      </ErrorProvider>
    </Router>
  );
};

export default App;
